import React, { useCallback, useEffect, useState } from 'react';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import '../../styles/AndamentoPeso.css';
import '../../styles/AndamentoPesoConsole.css';

/**
 * AndamentoPeso — il peso corporeo nel tempo.
 *
 * E' il gemello dell'andamento degli analiti negli esami, e come quello
 * e' disegnato a mano in SVG: sono una linea e dei punti, e una libreria
 * di grafici in piu' nel pacchetto va poi mantenuta per anni.
 *
 * QUELLO CHE NON C'E', ed e' voluto:
 *
 * - nessuna banda di normalita'. Gli analiti ce l'hanno perche' il
 *   laboratorio la dichiara sul referto; il peso «normale» non esiste
 *   come numero - dipende dall'altezza, dall'eta', dalla composizione -
 *   e disegnare una fascia verde dietro la linea vorrebbe dire inventare
 *   una soglia clinica che nessuno ha confermato.
 *
 * - nessun BMI. Il questionario lo calcola gia' dove c'e' anche
 *   l'altezza, e ricalcolarlo qui con un'altezza presa da un'altra data
 *   darebbe un secondo numero diverso dal primo.
 *
 * - nessun giudizio. La variazione si scrive col segno e basta: «-8,6 kg»
 *   e' un fatto, «ottimo risultato» no.
 */

const dataIta = (v) => {
    if (!v) return '';
    /* La data arriva dal server gia' come testo YYYY-MM-DD, apposta: un
       oggetto Date qui verrebbe letto nel fuso del browser e vicino a
       mezzanotte cambierebbe giorno. */
    const [a, m, g] = String(v).slice(0, 10).split('-');
    return g && m && a ? `${g}/${m}/${a}` : String(v);
};

const kg = (n) => Number(n).toLocaleString('it-IT', { maximumFractionDigits: 1 });

/* ------------------------------------------------------------------ */

const Grafico = ({ serie }) => {
    const L = 42, R = 14, T = 14, B = 26, W = 640, H = 190;

    const punti = serie.map((s) => ({
        x: new Date(`${String(s.data).slice(0, 10)}T12:00:00`).getTime(),
        y: s.peso,
        data: s.data,
        fonte: s.fonte,
    }));

    const valori = punti.map((p) => p.y);
    const min = Math.min(...valori);
    const max = Math.max(...valori);
    /* Con un margine proporzionale, una serie piatta (82,0 - 82,1 - 82,0)
       diventerebbe una montagna russa: il fondo scala minimo di due chili
       tiene le oscillazioni piccole visibilmente piccole. */
    const meta = Math.max((max - min) * 0.2, 1);
    const yMin = min - meta, yMax = max + meta;

    const xMin = Math.min(...punti.map((p) => p.x));
    const xMax = Math.max(...punti.map((p) => p.x));
    const px = (x) => L + ((x - xMin) / (xMax - xMin || 1)) * (W - L - R);
    const py = (y) => T + (1 - (y - yMin) / (yMax - yMin || 1)) * (H - T - B);

    const linea = punti.map((p, i) => `${i ? 'L' : 'M'}${px(p.x)},${py(p.y)}`).join(' ');

    return (
        <svg className="apz-grafico" viewBox={`0 0 ${W} ${H}`} role="img"
             aria-label={`Andamento del peso, da ${kg(punti[0].y)} a `
                         + `${kg(punti[punti.length - 1].y)} chilogrammi`}>
            <line x1={L} y1={H - B} x2={W - R} y2={H - B} className="apz-asse" />
            <line x1={L} y1={T} x2={L} y2={H - B} className="apz-asse" />

            <text x={L - 6} y={py(yMax) + 4} className="apz-tacca" textAnchor="end">{kg(yMax)}</text>
            <text x={L - 6} y={py(yMin) + 4} className="apz-tacca" textAnchor="end">{kg(yMin)}</text>

            <path d={linea} className="apz-linea" />

            {punti.map((p, i) => (
                <g key={i}>
                    <circle cx={px(p.x)} cy={py(p.y)} r="4.5" className="apz-punto" />
                    <title>{`${dataIta(p.data)}: ${kg(p.y)} kg — ${p.fonte}`}</title>
                </g>
            ))}

            <text x={L} y={H - 8} className="apz-tacca">{dataIta(punti[0].data)}</text>
            <text x={W - R} y={H - 8} className="apz-tacca" textAnchor="end">
                {dataIta(punti[punti.length - 1].data)}
            </text>
        </svg>
    );
};

/* ------------------------------------------------------------------ */

const AndamentoPeso = ({ pazienteId }) => {
    const [dati, setDati] = useState(null);
    const [errore, setErrore] = useState(null);

    const carica = useCallback(async () => {
        if (!pazienteId) return;
        try {
            const r = await api.get(`/questionari/peso/${pazienteId}`);
            setDati(r.data?.data || null);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere l’andamento del peso');
        }
    }, [pazienteId]);

    useEffect(() => { carica(); }, [carica]);

    if (errore) return <p className="apz-errore">{errore}</p>;
    if (!dati) return null;

    const { serie = [], scartate = 0, variazione } = dati;

    /* Nessuna misura: non si scrive niente. Un riquadro vuoto su ogni
       paziente che non ha mai pesato sarebbe rumore su tutte le cartelle
       per essere utile su poche. */
    if (!serie.length) return null;

    const ultima = serie[serie.length - 1];

    /* Con una misura sola non c'e' un andamento, ma c'e' un dato: si
       scrive quello, e si dice cosa serve per vedere la linea. */
    if (serie.length === 1) {
        return (
            <section className="apz-riquadro apz-riquadro--breve">
                <TrendingUp size={16} aria-hidden="true" />
                <span>
                    Ultimo peso registrato: <b>{kg(ultima.peso)} kg</b> del {dataIta(ultima.data)}.
                    Alla seconda misura compare l’andamento.
                </span>
            </section>
        );
    }

    return (
        <section className="apz-riquadro">
            <header className="apz-testata">
                <h3><TrendingUp size={17} aria-hidden="true" /> Andamento del peso</h3>
                <div className="apz-sintesi">
                    <span className="apz-ultimo">{kg(ultima.peso)} kg</span>
                    {variazione != null && (
                        <span className={`apz-variazione ${variazione < 0 ? 'giu' : variazione > 0 ? 'su' : ''}`}>
                            {variazione > 0 ? '+' : ''}{kg(variazione)} kg
                        </span>
                    )}
                    <span className="apz-periodo">
                        {serie.length} misure · dal {dataIta(serie[0].data)}
                    </span>
                </div>
            </header>

            <Grafico serie={serie} />

            {/* Le misure scartate si dicono. Restano scritte nel
                questionario da cui vengono, e nasconderle farebbe
                sembrare completo un grafico che non lo e'. */}
            {scartate > 0 && (
                <p className="apz-scarti">
                    <AlertTriangle size={13} aria-hidden="true" />
                    {scartate === 1
                        ? 'Una misura non è nel grafico perché fuori dall’intervallo 20–350 kg.'
                        : `${scartate} misure non sono nel grafico perché fuori dall’intervallo 20–350 kg.`}
                    {' '}Restano scritte nel questionario da cui vengono.
                </p>
            )}
        </section>
    );
};

export default AndamentoPeso;
